import { Layout, Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import useSidebar from './useSidebar';
import { MdOutlineAutoGraph } from 'react-icons/md';
import { RxDashboard } from 'react-icons/rx';
import { AiOutlineFundProjectionScreen, AiOutlineProject, AiOutlineGroup, AiFillCaretLeft, AiFillCaretRight } from 'react-icons/ai';
import { BiTask, BiUserCheck } from 'react-icons/bi';
import { FaQuestion } from 'react-icons/fa';
import React from 'react';

const { Sider } = Layout;

const Sidebar = ({ collapsed, setCollapsed }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { getCurrentRole, getCurrentUser } = useAuth();
    const role = getCurrentRole();
    const user = getCurrentUser();
    const [openKeys, setOpenKeys] = useState([]);
    const {
        getAllProject,
        project,
        getAllProjectSpmo,
        spmoProject,
    } = useSidebar();

    useEffect(() => {
        if (user) {
            getAllProject(user.ID);
            getAllProjectSpmo(user.ID);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggleCollapse = () => {
        const newCollapsedState = !collapsed;
        setCollapsed(newCollapsedState);
        localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
    };

    // Function to determine which menu item should be active based on current path
    const getSelectedKeys = () => {
        const currentPath = location.pathname;
        
        // Check for homepage routes
        if (currentPath.startsWith('/home')) {
            return ['/home'];
        }
        
        // Check for project routes with ID matching
        if (currentPath.startsWith('/projects/')) {
            const pathParts = currentPath.split('/');
            const projectId = pathParts[2]; // Extract project ID from /projects/:id
            if (projectId) {
                return [`/projects/${projectId}`];
            }
        }
        
        // Check for project summary rating routes with ID matching
        if (currentPath.startsWith('/projects-summary-rating/')) {
            const pathParts = currentPath.split('/');
            const projectId = pathParts[2]; // Extract project ID from /projects-summary-rating/:id
            if (projectId) {
                return [`/projects/${projectId}`];
            }
        }
        
        // Check for project summary all routes with ID matching
        if (currentPath.startsWith('/projects-summary-all/')) {
            const pathParts = currentPath.split('/');
            const projectId = pathParts[2]; // Extract project ID from /projects-summary-all/:id
            if (projectId) {
                return [`/projects/${projectId}`];
            }
        }
        
        // Check for SPMO routes
        if (currentPath.startsWith('/spmo-summary/')) {
            return [currentPath];
        }
        
        // Check for admin routes
        if (currentPath.startsWith('/project-list')) {
            return ['/project-list'];
        }
        
        if (currentPath.startsWith('/user-management')) {
            return ['/user-management'];
        }
        
        if (currentPath.startsWith('/bu-management')) {
            return ['/bu-management'];
        }
        
        if (currentPath.startsWith('/faq')) {
            return ['/faq'];
        }
        
        // Default exact match
        return [currentPath];
    };

    const items = [
        {
            key: '/home',
            icon: <RxDashboard style={{ fontSize: '18px' }} />,
            label: 'Homepage',
            onClick: () => navigate('/home')
        },
        ...(project && project.length > 0 ? [{
            key: 'my-task',
            icon: <AiOutlineFundProjectionScreen style={{ fontSize: '18px' }} />,
            label: 'My Task',
            children: project.map(p => ({
                key: `/projects/${p.ID}`,
                label: p.Name,
                onClick: () => navigate(`/projects/${p.ID}`)
            }))
        }] : []),
        ...(role && role.includes("spmo") && spmoProject && spmoProject.length > 0 ? [{
            key: 'my-approval',
            icon: <BiTask style={{ fontSize: '18px' }} />,
            label: 'My Approval',
            children: spmoProject.map(p => ({
                key: `/spmo-summary/${p.ID}`,
                label: p.Name,
                onClick: () => navigate(`/spmo-summary/${p.ID}`)
            }))
        }] : []),
        ...(role && (role.includes("admin") || role.includes("superadmin")) ? [
            {
                key: '/project-list',
                icon: <AiOutlineProject style={{ fontSize: '18px' }} />,
                label: 'Project Management',
                onClick: () => navigate('/project-list')
            },
            {
                key: '/user-management',
                icon: <BiUserCheck style={{ fontSize: '18px' }} />,
                label: 'User Management',
                onClick: () => navigate('/user-management')
            },
            {
                key: '/bu-management',
                icon: <AiOutlineGroup style={{ fontSize: '18px' }} />,
                label: 'Business Unit Management',
                onClick: () => navigate('/bu-management')
            }
        ] : []),
        {
            key: '/faq',
            icon: <FaQuestion style={{ fontSize: '18px' }} />,
            label: 'FAQ',
            onClick: () => navigate('/faq')
        },
    ];

    return (
        <Sider 
            trigger={null} 
            collapsible 
            collapsed={collapsed} 
            width={260}
            style={{ 
                background: '#fff',
                overflow: 'hidden', 
                height: '100vh', 
                position: 'fixed', 
                left: 0, 
                top: 0,
                bottom: 0,
                zIndex: 100,
                borderRight: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f0f0f0', flexShrink: 0, background: 'white' }}>
                 <MdOutlineAutoGraph style={{ fontSize: '32px', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }} color='#02B4CC' />
                 {!collapsed && <span style={{ marginLeft: 10, fontWeight: 'bold', background: 'linear-gradient(135deg, #02B4CC 70%, #0284C7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontSize: '16px' }}>Calibration System</span>}
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <style>
                    {`
                        .ant-menu-item-selected,
                        .ant-menu-submenu-selected > .ant-menu-submenu-title {
                            background: linear-gradient(135deg, #02B4CC 70%, #0284C7 100%) !important;
                            color: white !important;
                            font-weight: 600;
                        }
                        .ant-menu-item-selected .ant-menu-item-icon,
                        .ant-menu-submenu-selected > .ant-menu-submenu-title .ant-menu-item-icon {
                            color: white !important;
                        }
                        .ant-menu-item-selected::after {
                            border-right: 3px solid white !important;
                        }
                    `}
                </style>
                <Menu
                    mode="inline"
                    selectedKeys={getSelectedKeys()}
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    style={{ borderRight: 0 }}
                    items={items}
                />
            </div>

            <div style={{ 
                height: 50, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                borderTop: '1px solid #f0f0f0',
                flexShrink: 0,
                background: 'white'
            }}>
                 {collapsed ? 
                    <AiFillCaretRight 
                        onClick={handleToggleCollapse}
                        style={{ fontSize: '24px', cursor: 'pointer', background: 'linear-gradient(135deg, #02B4CC 70%, #0284C7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
                    /> : 
                    <AiFillCaretLeft 
                        onClick={handleToggleCollapse}
                        style={{ fontSize: '24px', cursor: 'pointer', background: 'linear-gradient(135deg, #02B4CC 70%, #0284C7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
                    />
                 }
            </div>
        </Sider>
    );
}

export default Sidebar;